import { Stack, Typography, useTheme } from "@mui/material"
import { ChartsTooltipContainer, useItemTooltip } from "@mui/x-charts"
import CircleIcon from "@mui/icons-material/Circle"
import { DateTime } from "luxon"
import { useTranslation } from "react-i18next"

import type { FieldQualityMap } from "types/quality.types"
import { getFieldFromSeriesId } from "widgets/plots/configuration/helpers"
import ChartTooltipCard from "widgets/plots/mui/ChartTooltipCard"

interface MuiScatterTooltipProps {
  trigger?: "axis" | "item" | "none" | undefined
  fieldQualityMap?: FieldQualityMap
}

const MuiScatterTooltip = ({ trigger, fieldQualityMap }: MuiScatterTooltipProps) => {
  const item = useItemTooltip()
  const { t } = useTranslation()
  const theme = useTheme()

  // Tooltip only for X axes, one axis of type "_time"
  // Not handling multiple axes or multiple time axes
  // Also not handling trigger "axis"
  const itemValue = item
    ? (item.value as unknown as { id: Date; x: Date; y: number })
    : null

  const formattedDate = itemValue?.x
    ? DateTime.fromJSDate(itemValue.x).toFormat("yyyy-MM-dd HH:mm")
    : ""

  const timeMillis = itemValue?.x ? DateTime.fromJSDate(itemValue.x).toMillis() : null

  const field = item ? getFieldFromSeriesId(String(item.identifier.seriesId)) : undefined
  const qualityInfo =
    fieldQualityMap && timeMillis !== null && field
      ? fieldQualityMap[timeMillis]?.[field]
      : undefined

  return (
    <ChartsTooltipContainer trigger={trigger}>
      {trigger === "item" && item && itemValue && (
        <ChartTooltipCard title={formattedDate}>
          <Stack spacing={0}>
            <Stack direction="row" gap={1} alignItems="center" width={"100%"}>
              <CircleIcon sx={{ fontSize: 8, color: item.color }} />
              <Stack
                direction="row"
                justifyContent={"space-between"}
                width={"100%"}
                gap={2}
              >
                <Typography>{item.label}</Typography>
                <Typography>{item.formattedValue}</Typography>
              </Stack>
            </Stack>
            {qualityInfo?.quality && qualityInfo.quality !== "healthy" && (
              <Typography
                variant="caption"
                sx={{
                  pl: 2,
                  fontSize: 10,
                  fontStyle: "italic",
                  color: theme.palette.quality[qualityInfo.quality]?.main,
                }}
              >
                {t(`quality.${qualityInfo.quality.toUpperCase()}`)}
              </Typography>
            )}
          </Stack>
        </ChartTooltipCard>
      )}
    </ChartsTooltipContainer>
  )
}

export default MuiScatterTooltip
